import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Box, Typography, CircularProgress, Alert, Paper, Button } from '@mui/material';
import { API_CONFIG } from '../../helpers/constants';

export interface OAuthCallbackPageProps {
  provider: string;
  redirectUri: string;
  loadingText: string;
  successTitle: string;
  successBody: string;
}

const OAuthCallbackPage: React.FC<OAuthCallbackPageProps> = ({
  provider,
  redirectUri,
  loadingText,
  successTitle,
  successBody,
}) => {
  const [searchParams] = useSearchParams();
  const navigate       = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);
  const cancelSentRef         = useRef(false);

  useEffect(() => {
    const handleCallback = async () => {
      try {
        const code  = searchParams.get('code');
        const state = searchParams.get('state');
        const err   = searchParams.get('error');

        if (err) {
          if (window.opener) {
            if (cancelSentRef.current) return;
            cancelSentRef.current = true;
            window.opener.postMessage(
              {
                type:             'oauth_complete',
                provider,
                success:          false,
                error:            err,
                errorDescription: searchParams.get('error_description') ?? undefined,
              },
              window.location.origin,
            );
            window.close();
            return;
          }
          setError(`OAuth error: ${err}`);
          setLoading(false);
          return;
        }

        if (!code) {
          setError('No authorization code received.');
          setLoading(false);
          return;
        }

        const res = await fetch(`${API_CONFIG.API_BASE_URL}/oauth/pkce/exchange`, {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify({ code, state, provider, redirect_uri: redirectUri }),
        });

        if (!res.ok) throw new Error(`Token exchange failed — status ${res.status}`);

        setLoading(false);

        if (window.opener) {
          window.opener.postMessage(
            { type: 'oauth_complete', provider, success: true },
            window.location.origin,
          );
          window.close();
        }
      } catch (e) {
        const msg = e instanceof Error ? e.message : 'An error occurred';
        setError(msg);
        setLoading(false);
        if (window.opener) {
          window.opener.postMessage(
            { type: 'oauth_complete', provider, success: false, error: msg },
            window.location.origin,
          );
        }
      }
    };

    handleCallback();
  }, [searchParams]);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          justifyContent: 'center', minHeight: '100vh', gap: 2,
        }}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" color="text.secondary">
          {loadingText}
        </Typography>
      </Box>
    );
  }

  if (window.opener && !error) return null;

  return (
    <Box
      sx={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', minHeight: '100vh', p: 3,
      }}
    >
      <Paper elevation={3} sx={{ p: 4, maxWidth: 600, width: '100%', textAlign: 'center' }}>
        {error ? (
          <>
            <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
            <Button variant="contained" onClick={() => window.opener ? window.close() : navigate('/supportSpace')}>
              Go Back
            </Button>
          </>
        ) : (
          <>
            <Typography variant="h4" gutterBottom color="success.main">
              {successTitle}
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              {successBody}
            </Typography>
            <Button variant="contained" onClick={() => navigate('/supportSpace')}>
              Continue
            </Button>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default OAuthCallbackPage;
